import { useState, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'active-audits-tour-completed';

export function useActiveAuditsTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="active-audits-header"]',
      title: 'Welcome to Active Audits',
      content: 'This is your central hub for managing all active audits. Monitor progress, track completion status, and take action on audits that need attention.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="active-audits-view-mode"]',
      title: 'View Modes',
      content: 'Switch between different views: List for detailed audit information, Card for a visual overview, or Kanban to see audits organized by status.',
      placement: 'left',
    },
    {
      target: '[data-tour="active-audits-new-button"]',
      title: 'Create New Audit',
      content: 'Click here to start creating a new audit. The wizard will guide you through selecting assets, setting schedules, and assigning team members.',
      placement: 'left',
    },
    {
      target: '[data-tour="active-audits-search"]',
      title: 'Search Audits',
      content: 'Quickly find audits by searching through audit names, descriptions, or assigned team members. The search updates in real-time as you type.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="active-audits-group-sort"]',
      title: 'Group and Sort',
      content: 'Organize your audits by grouping them by status, priority, or assignee. This helps you focus on specific audit categories.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="active-audits-list"]',
      title: 'Audits List',
      content: 'View all your active audits here. Select audits using the checkboxes to perform bulk actions. Click on any audit to view its full details and progress.',
      placement: 'top',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
